import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  ExperimentsService,
  GetExperimentResponse,
  GetTasterExperimentNextProductsResponse,
  TasterExperimentsService,
} from "api/client";

import { ROUTES } from "constants/routes";

import useHomeContainer from "features/Home/hooks/useHomeContainer";

import useMainLayoutContainer from "layouts/MainLayout/hooks/useMainLayoutContainer";
import axios from "axios";
import { getBaseUrl } from "../../../api/client/core/request";

export type ActivePageType =
  | "intro"
  | "products"
  | "tasting"
  | "waiting"
  | "finish"
  | "noPairs"
  | "kicked"
  | "left";

export type AnswersProps = {
  [key: string]: "A" | "B" | "same";
};

const useExperimentContainer = () => {
  const [experiment, setExperiment] = useState<GetExperimentResponse | null>(
    null
  );
  const [activePage, setActivePage] = useState<ActivePageType>("intro");
  const [loading, setLoading] = useState<boolean>(true);
  const [products, setProducts] = useState<string[]>([]);
  const [isExperimentLeaveModalOpen, setIsExperimentLeaveModalOpen] =
    useState<boolean>(false);
  const [questionsSection, setQuestionsSection] = useState<number>(1);
  const [answers, setAnswers] = useState<AnswersProps>({});
  const [isNeedHelp, setIsNeedHelp] = useState<boolean>(false);
  const [canStartSession, setCanStartSession] = useState<boolean>(false);
  const [errorToast, setErrorToast] = useState<string | null>(null);
  const [request, setSetRequest] = useState<boolean>(false);

  const { i18n, t } = useTranslation();

  const { tasterData, handleStationChange } = useMainLayoutContainer();

  const { experiments } = useHomeContainer();

  const { experimentId } = useParams();

  const navigate = useNavigate();

  const { id } = tasterData;

  const isValidExperiment = useMemo(
    () =>
      experiments &&
      experiments.length > 0 &&
      !!experiments.find((experiment) => experiment.id === experimentId),

    [experimentId, experiments]
  );

  useEffect(() => {
    if (experiments.length === 0 && activePage === "noPairs") {
      setActivePage("finish");
    }
  }, [experiments, activePage]);

  const currentLanguage = useMemo(() => {
    if (i18n.language === "he") {
      return "heb";
    } else if (i18n.language === "ar") {
      return "ar";
    }
    return "en"; // Default to English
  }, [i18n.language]);

  useEffect(() => {
    if (
      experiments &&
      experiments.length > 0 &&
      !!isValidExperiment &&
      experimentId
    ) {
      ExperimentsService.readExperimentV1ExperimentsIdGet(
        experimentId,
        currentLanguage
      )
        .then((response) => {
          setExperiment(response);

          if (response.status === "completed") {
            setActivePage("finish");
            setIsNeedHelp(false);
          }

          if (id) {
            const currentTaster = response.tasters?.find(
              (taster) => taster.id === id
            );

            if (!currentTaster) {
              setActivePage("kicked");
              setIsNeedHelp(false);
            }
          }
        })
        .catch(() => setErrorToast(t("error_toast_message")));
    } else {
      setInterval(() => setLoading(false), 3000);

      return clearInterval(setInterval(() => setLoading(false), 3000));
    }
  }, [experimentId, experiments, isValidExperiment, currentLanguage, id, t]);

  useEffect(() => {
    if (experiment && id) {
      const currentTaster = experiment.tasters?.find(
        (taster) => taster.id === id
      );

      if (!!currentTaster?.helpRequestedAt) {
        setIsNeedHelp(true);
      } else {
        setIsNeedHelp(false);
      }
    }
  }, [experiment, experimentId, experiments, id, isValidExperiment]);

  const redirectToBack = useCallback(() => {
    navigate(ROUTES.home);
  }, [navigate]);

  const isJoiningToTheExperiment = useMemo(() => {
    if (experiment && id) {
      const currentTaster = experiment.tasters?.find(
        (taster) => taster.id === id
      );

      return currentTaster && currentTaster.joinedAt;
    }

    return false;
  }, [experiment, id]);

  const handlePageChange = useCallback(
    (newPage: ActivePageType) => setActivePage(newPage),
    []
  );

  const getNextProducts = useCallback(async () => {
    if (
      experimentId &&
      experiment &&
      !!isValidExperiment &&
      isJoiningToTheExperiment &&
      activePage !== "finish"
    ) {
      TasterExperimentsService.getNextProductsV1TasterExperimentsNextProductsExperimentIdGet(
        experimentId
      )
        .then((response) => {
          if (response as GetTasterExperimentNextProductsResponse) {
            const result: GetTasterExperimentNextProductsResponse =
              response as GetTasterExperimentNextProductsResponse;

            const tasters = result.tasters;

            const products = tasters[0].products;

            const productCodes = products.map(
              (product) => product.product_code
            );

            if (productCodes && productCodes.length > 0) {
              setProducts(productCodes);
              setActivePage("products");
            }
          }
        })
        .catch((error) => {
          if (
            error.body.detail ===
            "request for batch but there is no prototypes submitted"
          ) {
            setActivePage("noPairs");
          } else {
            setErrorToast(t("error_toast_message"));
          }
        });
    }
  }, [
    activePage,
    experiment,
    experimentId,
    isJoiningToTheExperiment,
    isValidExperiment,
    t,
  ]);

  const getRoundsState = useCallback(() => {
    if (
      experimentId &&
      experiment &&
      !!isValidExperiment &&
      isJoiningToTheExperiment
    ) {
      TasterExperimentsService.getRoundsStateV1TasterExperimentsRoundsStateExperimentIdGet(
        experimentId
      )
        .then((response) => {
          if (activePage === "finish") return;

          if (
            response &&
            response.experiment_current_round > 0 &&
            activePage === "intro"
          ) {
            setCanStartSession(true);
            setLoading(false);
          }

          // if (response && response.experiment_current_round === 3) {
          //   setLoading(false);
          //   setActivePage("waiting");
          //   return;
          // }

          if (
            response &&
            response.experiment_current_round > 1 &&
            activePage !== "tasting" &&
            response.experiment_current_round !== response.taster_current_round
          ) {
            getNextProducts().finally(() => setLoading(false));
          }

          if (
            response &&
            response.experiment_current_round > 0 &&
            response.experiment_current_round === response.taster_current_round
          ) {
            setLoading(false);
            setActivePage("waiting");
          }
        })
        .catch(() => setErrorToast(t("error_toast_message")));
    }
  }, [
    activePage,
    experiment,
    experimentId,
    getNextProducts,
    isJoiningToTheExperiment,
    isValidExperiment,
    t,
  ]);

  useEffect(() => {
    if (activePage !== "intro" && activePage !== "finish") {
      const interval = setInterval(() => getRoundsState(), 10000);

      return () => clearInterval(interval);
    }

    if (activePage === "intro") {
      getRoundsState();
    }
  }, [activePage, getRoundsState]);

  useEffect(() => {
    if (
      experiments &&
      experiments.length > 0 &&
      !!isValidExperiment &&
      experiment &&
      experimentId
    ) {
      const getExperiment = () =>
        ExperimentsService.readExperimentV1ExperimentsIdGet(
          experimentId,
          currentLanguage
        )
          .then((response) => {
            if (response.status === "completed") {
              setActivePage("finish");
              setIsNeedHelp(false);
            }

            const currentTaster = response.tasters?.find(
              (taster) => taster.id === id
            );

            if (!currentTaster) {
              setActivePage("kicked");
              setIsNeedHelp(false);
            }
          })
          .catch(() => setErrorToast(t("error_toast_message")));
      const interval = setInterval(() => getExperiment(), 10000);

      return () => clearInterval(interval);
    }
  }, [
    experimentId,
    experiments,
    isValidExperiment,
    currentLanguage,
    id,
    experiment,
    t,
  ]);

  const handleLeaveModalOpen = useCallback(
    () => setIsExperimentLeaveModalOpen(true),
    []
  );

  const handleLeaveModalClose = useCallback(
    () => setIsExperimentLeaveModalOpen(false),
    []
  );

  const handleExperimentLeave = useCallback(() => {
    if (
      experimentId &&
      experiment &&
      !!isValidExperiment &&
      isJoiningToTheExperiment
    ) {
      TasterExperimentsService.leaveExperimentV1TasterExperimentsExperimentIdDelete(
        experimentId
      )
        .then(() => {
          navigate(ROUTES.home);
          setProducts([]);
          setActivePage("intro");
          setIsNeedHelp(false);
        })
        .catch(() => setErrorToast(t("error_toast_message")));
    }
  }, [
    experiment,
    experimentId,
    isJoiningToTheExperiment,
    isValidExperiment,
    navigate,
    t,
  ]);

  const questions = useMemo(() => {
    let currentLanguage = "en"; // Default to English
    if (i18n.language === "he") {
      currentLanguage = "heb";
    } else if (i18n.language === "ar") {
      currentLanguage = "ar";
    }

    return experiment?.questionnaire?.form?.map((questionnaire) => ({
      instructions: questionnaire.description,
      questions: questionnaire.questions.map((question) => ({
        id: question.id,
        text: question.content[currentLanguage] || question.content["en"], // Fallback to English if the selected language is not available
      })),
    }));
  }, [experiment?.questionnaire, i18n.language]); // Dependency on the language

  const handleAnswersChange = useCallback(
    (questionId: string) => (answer: "A" | "B" | "same") => {
      setAnswers((prev) => ({
        ...prev,
        [questionId]: answer,
      }));
    },
    []
  );

  const postTasterStation = useCallback(
    async (newStation: string) => {
      if (experimentId && id) {
        setSetRequest(true);

        try {
          await axios.post(
            `${getBaseUrl()}/v1/taster-experiments/station/${experimentId}?taster_id=${id}`,
            { station: newStation }
          );
          handleStationChange?.(newStation);
        } catch (error) {
          console.error("Error setting taster station", error);
        } finally {
          setSetRequest(false);
        }
      }
    },
    [experimentId, id, handleStationChange]
  );

  const onNeedHelpInfoHide = useCallback(() => setIsNeedHelp(false), []);

  const handleHelpAsk = useCallback(() => {
    if (experimentId) {
      TasterExperimentsService.askForHelpV1TasterExperimentsAskForHelpExperimentIdPost(
        experimentId
      )
        .then(() => setIsNeedHelp(true))
        .catch(() => setErrorToast(t("error_toast_message")));
    }
  }, [experimentId, t]);

  const handleHelpCancel = useCallback(() => {
    if (experimentId) {
      TasterExperimentsService.cancelHelpV1TasterExperimentsCancelHelpExperimentIdPost(
        experimentId
      )
        .then(() => setIsNeedHelp(false))
        .catch(() => setErrorToast(t("error_toast_message")));
    }
  }, [experimentId, t]);

  const handleQuestionsSectionNextChange = useCallback(
    () => setQuestionsSection((prev) => prev + 1),
    []
  );

  const handleQuestionsSectionPrevChange = useCallback(
    () => setQuestionsSection((prev) => prev - 1),
    []
  );

  const handleAnswersSubmit = useCallback(() => {
    if (experimentId) {
      TasterExperimentsService.submitAnswersV1TasterExperimentsSubmitExperimentIdPost(
        experimentId,
        { answers, product_codes: products }
      )
        .then(() => {
          setActivePage("waiting");
          setAnswers({});
          setQuestionsSection(1);
        })
        .catch(() => setErrorToast(t("error_toast_message")));
    }
  }, [answers, experimentId, products, t]);

  const handleAnswersBtnClick = useMemo(
    () =>
      questionsSection === questions?.length
        ? handleAnswersSubmit
        : handleQuestionsSectionNextChange,
    [
      handleAnswersSubmit,
      handleQuestionsSectionNextChange,
      questions?.length,
      questionsSection,
    ]
  );

  const handleToastClose = useCallback(() => setErrorToast(null), []);

  return {
    experimentId,
    experiment,
    isJoiningToTheExperiment,
    loading: loading,
    activePage,
    products,
    isExperimentLeaveModalOpen,
    questions,
    answers,
    isNeedHelp,
    canStartSession,
    questionsSection,
    request,
    errorToast,
    redirectToBack,
    handlePageChange,
    getNextProducts,
    handleLeaveModalOpen,
    handleLeaveModalClose,
    handleExperimentLeave,
    handleAnswersChange,
    handleAnswersBtnClick,
    onNeedHelpInfoHide,
    handleHelpAsk,
    handleQuestionsSectionPrevChange,
    handleHelpCancel,
    tasterData,
    postTasterStation,
    handleToastClose,
  };
};

export default useExperimentContainer;
